import React, { createContext, useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { supportedCurrencies, detectDefaultCurrency } from '../utils/currencyUtils';

// Exchange rates API (free tier)
const EXCHANGE_RATE_API = 'https://open.er-api.com/v6/latest/USD';

// Create the context
export const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const user = useSelector((state) => state.auth?.user);
  // Moneda default de toda la app: la local del cliente (por país de
  // perfil si ya cargó, si no por idioma/zona horaria del navegador — ver
  // detectDefaultCurrency), no USD a secas. Un cliente en Perú ve montos
  // en soles por defecto en vez de dólares.
  const [currency, setCurrency] = useState(() => detectDefaultCurrency());
  const [exchangeRates, setExchangeRates] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch exchange rates on mount and when currency changes
  useEffect(() => {
    const fetchExchangeRates = async () => {
      setLoading(true);
      try {
        // In a production app, you would use your own API key and properly handle rate limits
        // For this demo, we're using a free, public API
        const response = await fetch(EXCHANGE_RATE_API);
        const data = await response.json();
        
        if (data && data.rates) {
          setExchangeRates(data.rates);
          setLastUpdated(new Date(data.time_last_update_utc));
        } else {
          throw new Error('Invalid exchange rate data received');
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching exchange rates:', err);
        setError('Failed to fetch exchange rates. Using default values.');
        
        // Use fallback/mock exchange rates if API fails
        setExchangeRates({
          USD: 1.0,
          EUR: 0.91,
          GBP: 0.78,
          JPY: 148.73,
          BRL: 5.05,
          INR: 83.14,
          // Add more default rates as needed
        });
        setLastUpdated(new Date());
      } finally {
        setLoading(false);
      }
    };

    fetchExchangeRates();
    
    // Refresh rates every hour if the app is continuously running
    const intervalId = setInterval(fetchExchangeRates, 3600000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Si el cliente ya eligió una moneda explícitamente antes, esa preferencia
  // manda (localStorage). Si no, en cuanto se conoce el país de su perfil
  // (llega después del mount, vía Redux), se refina el default detectado
  // al vuelo con el idioma/zona horaria del navegador.
  useEffect(() => {
    const savedCurrency = localStorage.getItem('preferredCurrency');
    if (savedCurrency && supportedCurrencies[savedCurrency]) {
      setCurrency(savedCurrency);
    } else {
      setCurrency(detectDefaultCurrency(user?.country));
    }
  }, [user?.country]);

  // Save currency preference to localStorage
  const changeCurrency = (newCurrency) => {
    if (supportedCurrencies[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem('preferredCurrency', newCurrency);
    }
  };

  // Convert amount from USD to selected currency
  const convertAmount = (amountInUSD) => {
    if (typeof amountInUSD !== 'number' || isNaN(amountInUSD)) {
      return 0;
    }
    
    const rate = exchangeRates[currency] || 1;
    return amountInUSD * rate;
  };

  // Format amount according to the selected currency's formatting rules
  const formatAmount = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      amount = 0;
    }
    
    const currencyInfo = supportedCurrencies[currency] || supportedCurrencies.USD;
    
    const options = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currencyInfo.decimalPlaces,
      maximumFractionDigits: currencyInfo.decimalPlaces,
    };
    
    return new Intl.NumberFormat('en-US', options).format(amount);
  };

  // Convert and format in one step (most common use case)
  const convertAndFormat = (amountInUSD) => {
    const converted = convertAmount(amountInUSD);
    return formatAmount(converted);
  };

  return (
    <CurrencyContext.Provider 
      value={{ 
        currency, 
        exchangeRates,
        lastUpdated,
        loading,
        error,
        changeCurrency,
        convertAmount,
        formatAmount,
        convertAndFormat
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook for using the currency context
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
